import { useId, useRef, useState, type ChangeEvent } from 'react';
import { uploadMedia, type UploadMediaOptions } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { corsMediaUrl } from '@/lib/corsMedia';
import { Download, Loader2, UploadCloud } from 'lucide-react';

type PhotoUploadFieldProps = {
  label: string;
  helper?: React.ReactNode;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
  category: UploadMediaOptions['category'];
  identifier?: string;
  accountSlug?: string; // Account slug for multi-tenant storage path prefixing
  uploadAuth?: UploadMediaOptions['auth']; // For anonymous invite/stub-link upload flows
  imageFocus?: string; // CSS object-position value for preview (e.g. 'top', 'center', 'bottom')
  preferredRatio?: 'landscape' | 'portrait';
  onOrientationMismatch?: (info: { mismatched: boolean; isSquare: boolean }) => void;
  compact?: boolean; // Smaller preview for admin dialogs
  allowBypassSizeLimit?: boolean; // Show bypass checkbox (admin-only)
  hideUrlInput?: boolean; // Hide the manual URL text input (artist-facing flows: upload only)
  required?: boolean; // Render a required marker next to the label
};

function checkMismatch(
  imgWidth: number,
  imgHeight: number,
  preferred: 'landscape' | 'portrait',
): boolean {
  if (imgWidth === imgHeight) return true; // Square never matches a preferred orientation
  const isPortrait = imgHeight > imgWidth;
  return preferred === 'landscape' ? isPortrait : !isPortrait;
}

function warningMessage(preferred: 'landscape' | 'portrait', isSquare: boolean): string {
  const target = preferred === 'landscape' ? 'landscape' : 'portrait';
  if (isSquare) {
    return `Heads up: this square image will be cropped to ${target} on the website.`;
  }
  if (preferred === 'landscape') {
    return 'Heads up: this portrait image will be cropped to landscape on the website, which can cut off heads.';
  }
  return 'Heads up: this landscape image will be cropped to portrait on the website, which can cut off the sides.';
}

export function PhotoUploadField({
  label,
  helper,
  placeholder,
  value,
  onChange,
  onUploadStateChange,
  category,
  identifier,
  accountSlug,
  uploadAuth,
  imageFocus,
  preferredRatio = 'landscape',
  onOrientationMismatch,
  compact = false,
  allowBypassSizeLimit = false,
  hideUrlInput = false,
  required = false,
}: PhotoUploadFieldProps) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [bypassSizeLimit, setBypassSizeLimit] = useState(false);

  const handleManualChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setWarning(null);
    onChange(event.target.value);
  };

  const handleFilePick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 15;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    if (!(allowBypassSizeLimit && bypassSizeLimit) && file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setIsUploading(true);
    onUploadStateChange?.(true);
    setError(null);
    setWarning(null);

    // Check orientation against preference
    try {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = objectUrl;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); });
      URL.revokeObjectURL(objectUrl);
      const isSquare = img.naturalWidth === img.naturalHeight;
      const mismatched = checkMismatch(img.naturalWidth, img.naturalHeight, preferredRatio);
      if (mismatched) {
        setWarning(warningMessage(preferredRatio, isSquare));
      }
      onOrientationMismatch?.({ mismatched, isSquare });
    } catch {
      // Ignore dimension check errors, proceed with upload
    }

    try {
      const result = await uploadMedia(file, {
        category,
        identifier,
        fileName: file.name,
        accountSlug,
        auth: uploadAuth,
      });
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
      // Reset input value so the same file can be re-selected if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearPhoto = () => {
    setError(null);
    setWarning(null);
    onChange('');
    onOrientationMismatch?.({ mismatched: false, isSquare: false });
  };

  const handlePreviewLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const isSquare = img.naturalWidth === img.naturalHeight;
    const mismatched = checkMismatch(img.naturalWidth, img.naturalHeight, preferredRatio);
    onOrientationMismatch?.({ mismatched, isSquare });
    if (mismatched) {
      setWarning(warningMessage(preferredRatio, isSquare));
    }
  };

  const downloadPhoto = async () => {
    if (!value) return;
    try {
      // corsMediaUrl: keeps this read off the cache entry the <img> preview
      // populated without CORS headers, which otherwise fails the fetch.
      const response = await fetch(corsMediaUrl(value), { mode: 'cors' });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Extract filename from URL or use a default
      const urlPath = new URL(value).pathname;
      const filename = urlPath.split('/').pop() || 'photo.jpg';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Last resort: hand the URL to the browser. Cross-origin `download` is
      // ignored so this opens the image rather than saving it, but the photo
      // stays reachable instead of the button just failing.
      window.open(value, '_blank', 'noopener');
      setError('Could not save the photo directly — opened it in a new tab instead.');
    }
  };

  const previewAspectClass = preferredRatio === 'portrait' ? 'aspect-[3/4]' : 'aspect-[3/2]';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </label>
        <div className="flex items-center gap-2">
          {value && (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={downloadPhoto}>
                <Download className="mr-1 h-4 w-4" />
                Download
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearPhoto}>
                Remove
              </Button>
            </>
          )}
          <Button type="button" variant="outline" size="sm" onClick={handleFilePick} disabled={isUploading}>
            {isUploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="mr-2 h-4 w-4" />
            )}
            {isUploading ? 'Uploading…' : 'Upload photo'}
          </Button>
        </div>
      </div>

      {value ? (
        <div className={`overflow-hidden rounded-md border${compact ? ' max-w-[280px]' : ''}`}>
          <img
            src={value}
            alt={`${label} preview`}
            className={`${previewAspectClass} w-full object-cover`}
            style={imageFocus ? { objectPosition: imageFocus } : undefined}
            loading="lazy"
            onLoad={handlePreviewLoad}
          />
        </div>
      ) : (
        <div className={`flex ${previewAspectClass} ${compact ? 'max-w-[280px]' : 'w-full'} items-center justify-center rounded-md border border-dashed border-input text-sm text-muted-foreground`}>
          No photo selected
        </div>
      )}

      {!hideUrlInput && (
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={handleManualChange}
          placeholder={placeholder ?? 'https://'}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      )}
      {helper && !value && <p className="text-xs text-muted-foreground">{helper}</p>}
      {allowBypassSizeLimit && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={bypassSizeLimit}
            onChange={(e) => setBypassSizeLimit(e.target.checked)}
            className="rounded border-input"
          />
          Bypass 15 MB size limit
        </label>
      )}
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleFileChange}
      />
    </div>
  );
}
